import React, { useState } from "react";
import { TrendReportContext, initialState } from "./TrendReportContext";
import { ISelectedTrendGraphItem, ITrendReportContextProps } from "./TrendReportContextInterfaces";

export const TrendReportContextProvider: React.FC<ITrendReportContextProps> = ({ children }) => {
    const [showAllProductionLines, setShowAllProductionLines] = useState<boolean>(true);
    const [selectedPreviousProductionLine, setSelectedPreviousProductionLine] = useState<
        ISelectedTrendGraphItem | undefined
    >(initialState.selectedProductionLine);
    const [selectedProductionLine, setSelectedProductionLine] = useState(initialState.selectedProductionLine);
    const [millCountryCode, setMillCountryCode] = useState<string>("");

    const [showAllFailureTypes, setShowAllFailureTypes] = useState<boolean>(true);
    const [selectedFailureType, setSelectedFailureType] = useState<ISelectedTrendGraphItem | undefined>(
        initialState.selectedFailureType,
    );
    const [selectedPreviousFailureType, setSelectedPreviousFailureType] = useState<ISelectedTrendGraphItem | undefined>(
        initialState.selectedPreviousFailureType,
    );

    const [showAllMachineCenters, setShowAllMachineCenters] = useState<boolean>(true);
    const [selectedMachineCenter, setSelectedMachineCenter] = useState<ISelectedTrendGraphItem | undefined>(
        initialState.selectedMachineCenter,
    );
    const [selectedPreviousMachineCenter, setSelectedPreviousMachineCenter] = useState<
        ISelectedTrendGraphItem | undefined
    >(initialState.selectedPreviousMachineCenter);

    const [showAllFailureCodes, setShowAllFailureCodes] = useState<boolean>(true);
    const [selectedFailureCode, setSelectedFailureCode] = useState<ISelectedTrendGraphItem | undefined>(
        initialState.selectedFailureCode,
    );
    const [selectedPreviousFailureCode, setSelectedPreviousFailureCode] = useState<ISelectedTrendGraphItem | undefined>(
        initialState.selectedPreviousFailureCode,
    );

    const [showAllSecondaryFailureCodes, setShowAllSecondaryFailureCodes] = useState<boolean>(true);
    const [selectedSecondaryFailureCode, setSelectedSecondaryFailureCode] = useState<
        ISelectedTrendGraphItem | undefined
    >(initialState.selectedSecondaryFailureCode);
    const [selectedPreviousSecondaryFailureCode, setSelectedPreviousSecondaryFailureCode] = useState<
        ISelectedTrendGraphItem | undefined
    >(initialState.selectedPreviousSecondaryFailureCode);

    const [totalDowntimeSeconds, setTotalDowntimeSeconds] = useState<number | undefined>(undefined);

    const handleSelectedProductionLineChange = (value: ISelectedTrendGraphItem | undefined) => {
        if (showAllProductionLines) {
            setShowAllProductionLines(false);
        }
        setSelectedProductionLine(value);
        setSelectedFailureType(undefined);
        setSelectedPreviousFailureType(undefined);
        setSelectedMachineCenter(undefined);
        setSelectedPreviousMachineCenter(undefined);
        setSelectedFailureCode(undefined);
        setSelectedPreviousFailureCode(undefined);
        setSelectedSecondaryFailureCode(undefined);
        setSelectedPreviousSecondaryFailureCode(undefined);
        setShowAllFailureTypes(true);
        setShowAllMachineCenters(true);
        setShowAllFailureCodes(true);
        setShowAllSecondaryFailureCodes(true);
    };

    const handleSelectedPreviousProductionLineChange = (value: ISelectedTrendGraphItem | undefined) => {
        if (showAllProductionLines) {
            setShowAllProductionLines(false);
        }
        setSelectedPreviousProductionLine(value);
    };

    const handleShowAllProductionLinesChange = (value: boolean) => {
        setShowAllProductionLines(value);
    };

    const handleSetMillCountryCode = (countryCode: string) => {
        setMillCountryCode(countryCode);
    };

    const handleSetSelectedFailureType = (value: ISelectedTrendGraphItem | undefined) => {
        if (value?.id === selectedFailureType?.id) return;
        setSelectedFailureType(value);
        setSelectedMachineCenter(undefined);
        setSelectedPreviousMachineCenter(undefined);
        setSelectedFailureCode(undefined);
        setSelectedPreviousFailureCode(undefined);
        setSelectedSecondaryFailureCode(undefined);
        setSelectedPreviousSecondaryFailureCode(undefined);
        setShowAllMachineCenters(true);
        setShowAllFailureCodes(true);
        setShowAllSecondaryFailureCodes(true);
    };

    const handleSetTotalDowntimeSeconds = (value: number) => {
        setTotalDowntimeSeconds(value);
    };

    const handleShowAllFailureTypesChange = (value: boolean) => {
        setShowAllFailureTypes(value);
    };

    const handleSelectedPreviousFailureTypeChange = (value: ISelectedTrendGraphItem | undefined) => {
        if (value?.id === selectedPreviousFailureType?.id) return;
        setSelectedPreviousFailureType(value);
    };

    const handleShowAllMachineCentersChange = (value: boolean) => {
        setShowAllMachineCenters(value);
    };

    const handleSelectedPreviousMachineCenterChange = (value: ISelectedTrendGraphItem | undefined) => {
        if (value?.id === selectedPreviousMachineCenter?.id) return;
        setSelectedPreviousMachineCenter(value);
    };

    const handleSetSelectedMachineCenter = (value: ISelectedTrendGraphItem | undefined) => {
        if (value?.id === selectedMachineCenter?.id) return;
        setSelectedMachineCenter(value);
    };

    const handleSetSelectedFailureCode = (value: ISelectedTrendGraphItem | undefined) => {
        if (value?.id === selectedFailureCode?.id) return;
        setSelectedFailureCode(value);
        setSelectedSecondaryFailureCode(undefined);
        setSelectedPreviousSecondaryFailureCode(undefined);
        setShowAllSecondaryFailureCodes(true);
    };

    const handleSelectedPreviousFailureCodeChange = (value: ISelectedTrendGraphItem | undefined) => {
        if (value?.id === selectedPreviousFailureCode?.id) return;
        setSelectedPreviousFailureCode(value);
    };

    const handleShowAllFailureCodesChange = (value: boolean) => {
        setShowAllFailureCodes(value);
    };

    const handleSetSelectedSecondaryFailureCode = (value: ISelectedTrendGraphItem | undefined) => {
        if (value?.id === selectedFailureCode?.id) return;
        setSelectedSecondaryFailureCode(value);
    };

    const handleSelectedPreviousSecondaryFailureCodeChange = (value: ISelectedTrendGraphItem | undefined) => {
        if (value?.id === selectedPreviousFailureCode?.id) return;
        setSelectedPreviousSecondaryFailureCode(value);
    };

    const handleShowAllSecondaryFailureCodesChange = (value: boolean) => {
        setShowAllSecondaryFailureCodes(value);
    };

    const providerValue = {
        showAllProductionLines,
        handleShowAllProductionLinesChange,
        selectedProductionLine,
        handleSelectedProductionLineChange,
        selectedPreviousProductionLine,
        handleSelectedPreviousProductionLineChange,
        millCountryCode,
        handleSetMillCountryCode,
        showAllFailureTypes,
        handleShowAllFailureTypesChange,
        selectedPreviousFailureType,
        handleSelectedPreviousFailureTypeChange,
        selectedFailureType,
        handleSetSelectedFailureType,
        totalDowntimeSeconds,
        handleSetTotalDowntimeSeconds,
        showAllMachineCenters,
        handleShowAllMachineCentersChange,
        selectedPreviousMachineCenter,
        handleSelectedPreviousMachineCenterChange,
        selectedMachineCenter,
        handleSetSelectedMachineCenter,
        showAllFailureCodes,
        handleShowAllFailureCodesChange,
        selectedFailureCode,
        handleSetSelectedFailureCode,
        selectedPreviousFailureCode,
        handleSelectedPreviousFailureCodeChange,
        showAllSecondaryFailureCodes,
        handleShowAllSecondaryFailureCodesChange,
        selectedSecondaryFailureCode,
        handleSetSelectedSecondaryFailureCode,
        selectedPreviousSecondaryFailureCode,
        handleSelectedPreviousSecondaryFailureCodeChange,
    };

    return <TrendReportContext.Provider value={providerValue}>{children}</TrendReportContext.Provider>;
};
